/**
 * Security health: cosa, del parco, merita uno sguardo.
 *
 * ## Perché non sta dentro «Job scansioni»
 *
 * Perché sono due domande diverse fatte in due momenti diversi. La pagina delle
 * scansioni risponde a «cosa c'è», e chi la apre sta inventariando; questa
 * risponde a «cosa mi preoccupa». Messe nello stesso elenco, la seconda si
 * vedrebbe solo per caso mentre passa la prima — cioè quasi mai, perché non è
 * per quello che si era andati lì.
 *
 * ## Perché non è un elenco di allarmi
 *
 * ntopng ha un motore di allarmi e una console che li mostra: ricopiarne il
 * flusso qui produrrebbe una seconda schermata sempre in ritardo sulla prima.
 * Questa pagina mostra **gli asset segnalati**, con il motivo, ed è la stessa
 * cosa vista dal lato che una CMDB può sostenere: non «cos'è successo alle
 * 03:14», ma «di quali macchine si sta parlando».
 */
import { TestoConKev, cveDaProva } from '../components/Kev'
import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { ConfigurationItem, DiscoveryKind, EndOfSupport, SecurityFinding, SecurityRisk } from '../api/types'
import DiscoveryForm from '../components/DiscoveryForm'
import { ModuloInVista } from '../components/ModuloInVista'
import { PageSection, SectionedPage, Card, EmptyState, ErrorBanner, formatDateTime, Spinner, StatCard } from '../components/common'
import { Donut } from '../components/charts'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { maiuscola, t } from '../i18n/lingua'
import { azioniDa } from '../security/azioni'
import type { Azione } from '../security/azioni'
import { CRITICALITY_LABEL } from './Services'

/** L'attributo con cui la sonda marca un host segnalato da ntopng. */
const ATTRIBUTO_SEGNALATO = 'ntopng_flagged'

export default function SecurityHealth() {
  const [aperto, setAperto] = useState<{ kind: DiscoveryKind; mode: 'run' | 'schedule' } | null>(
    null,
  )
  const [avviso, setAvviso] = useState<string | null>(null)

  const kinds = useAsync(() => api.discoveryKinds(), [])
  const credenziali = useAsync(() => api.credentials(), [])
  const gruppi = useAsync(() => api.credentialGroups(), [])

  // Rischio e reperti si caricano QUI, una volta: li leggono il quadro in
  // cima, «Cosa fare» e le sezioni sotto. Ognuno col suo fetch sarebbero tre
  // richieste per gli stessi dati — e tre momenti diversi della verità.
  const rischio = useAsync(() => api.securityRisk(), [])
  const [conAccettati, setConAccettati] = useState(false)
  const reperti = useAsync(() => api.securityFindings(conAccettati), [conAccettati])
  const segnalati = useAsync(
    () => api.cis({ attribute: ATTRIBUTO_SEGNALATO, value: 'true', limit: 500 }),
    [],
  )

  // I tipi della famiglia «security», presi dal catalogo e non elencati qui.
  // Scrivere «ntopng» in questa pagina avrebbe funzionato oggi e avrebbe fatto
  // sì che una seconda sorgente, domani, non comparisse — senza che niente lo
  // segnali, perché la pagina continuerebbe a disegnarsi.
  const sorgenti = (kinds.data ?? []).filter((k) => k.family === 'security')

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Security health')}</h1>
          <p className="page__subtitle">
            {t('aggrega gli asset segnalati dalle sorgenti di sicurezza e le evidenze che ne determinano la segnalazione.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={kinds.error ?? segnalati.error} />
      {avviso && <div className="banner banner--ok">{avviso}</div>}

      <SectionedPage
        sections={[
          { id: 'quadro', label: t('Quadro') },
          { id: 'cosa-fare', label: t('Cosa fare') },
          { id: 'rischio', label: t('Rischio') },
          { id: 'reperti', label: t('Dettaglio rischio') },
          { id: 'fuori-supporto', label: t('Fuori supporto') },
          { id: 'segnalati', label: t('Asset segnalati') },
          { id: 'sorgenti', label: t('Sorgenti') },
        ]}
      >

      {/* Il quadro prima di tutto: chi apre la pagina non sa ancora se deve
          preoccuparsi, e la risposta deve stare in un colpo d'occhio — la
          ruota del rischio e i tre numeri che decidono la giornata. */}
      <PageSection id="quadro">
        <Quadro rischio={rischio.data} reperti={reperti.data ?? []} />
      </PageSection>

      {/* Le azioni subito dopo il quadro e PRIMA dell'elenco dei reperti:
          «cosa faccio» viene prima di «cosa c'è», perché l'elenco si legge per
          controllare, non per decidere. */}
      <PageSection id="cosa-fare">
        <CosaFare righe={(reperti.data ?? []).filter((r) => !r.accepted)} />
      </PageSection>


      <PageSection id="rischio">
        <Rischio dati={rischio.data} caricamento={rischio.loading} />
      </PageSection>

      <PageSection id="reperti">
        <Reperti
          stato={reperti}
          conAccettati={conAccettati}
          onConAccettati={setConAccettati}
        />
      </PageSection>

      <PageSection id="fuori-supporto">
        <FineSupporto />
      </PageSection>

      <PageSection id="segnalati">
        <Segnalati righe={segnalati.data} caricamento={segnalati.loading} />
      </PageSection>

      <PageSection id="sorgenti">
      <Card title={t('Sorgenti')}>
        {kinds.loading && !kinds.data ? (
          <Spinner />
        ) : sorgenti.length === 0 ? (
          <EmptyState
            title={t('Nessuna sorgente di sicurezza')}
            hint={t('Il piano in uso non comprende nessuna sorgente di questo tipo.')}
          />
        ) : (
          <>
            <div className="kindgrid">
              {sorgenti.map((entry) => (
                <Fragment key={entry.kind}>
                <div className={`kindcard${aperto?.kind.kind === entry.kind ? ' kindcard--open' : ''}`}>
                  <div className="kindcard__head">
                    <span className="kindcard__name">{t(entry.label)}</span>
                    {!entry.licensed && (
                      <span className="badge badge--muted">{t('non compreso nella licenza')}</span>
                    )}
                  </div>
                  <p className="kindcard__hint">{t(entry.hint)}</p>
                  <div className="kindcard__actions">
                    <button
                      className="secondary"
                      disabled={!entry.licensed}
                      onClick={() =>
                        setAperto(
                          aperto?.kind.kind === entry.kind && aperto.mode === 'run'
                            ? null
                            : { kind: entry, mode: 'run' },
                        )
                      }
                    >
                      {t('Avvia ora')}
                    </button>
                    <button
                      className="secondary"
                      disabled={!entry.licensed}
                      onClick={() =>
                        setAperto(
                          aperto?.kind.kind === entry.kind && aperto.mode === 'schedule'
                            ? null
                            : { kind: entry, mode: 'schedule' },
                        )
                      }
                    >
                      {t('Pianifica')}
                    </button>
                  </div>
                </div>
                {/* Subito sotto la tessera cliccata, a tutta larghezza, e in
                    vista: in fondo al riquadro finiva fuori dallo schermo. */}
                {aperto?.kind.kind === entry.kind && (
                  <ModuloInVista className="kindform kindform--inline">
                    <h3 className="kindform__title">
                      {aperto.mode === 'run'
                        ? t('Avvia: {tipo}', { tipo: t(aperto.kind.label) })
                        : t('Pianifica: {tipo}', { tipo: t(aperto.kind.label) })}
                    </h3>
                    <DiscoveryForm
                      kind={aperto.kind}
                      mode={aperto.mode}
                      credentials={credenziali.data ?? []}
                      credentialGroups={gruppi.data ?? []}
                      onCancel={() => setAperto(null)}
                      onDone={(messaggio) => {
                        setAvviso(messaggio)
                        setAperto(null)
                        // La raccolta è appena partita: i segnalati arriveranno con
                        // l'ingest, non adesso. Si ricarica lo stesso perché un
                        // giro precedente potrebbe averne già portati.
                        segnalati.reload()
                      }}
                    />
                  </ModuloInVista>
                )}
              </Fragment>
              ))}
            </div>

          </>
        )}
      </Card>
      </PageSection>
      </SectionedPage>
    </>
  )
}

/**
 * Il quadro: la ruota del rischio e i tre numeri che decidono la giornata.
 *
 * Chi apre la pagina non sa ancora se deve preoccuparsi. La risposta deve
 * stare in un colpo d'occhio: quanti asset per fascia, quante vulnerabilità
 * **già sfruttate attivamente (CISA KEV)** — il numero che cambia l'ordine delle cose — e
 * quanto è fresca la valutazione, perché «nessun rilievo» di tre mesi fa non è
 * «nessun rilievo».
 */
function Quadro({
  rischio,
  reperti,
}: {
  rischio: SecurityRisk | undefined | null
  reperti: SecurityFinding[]
}) {
  const sintesi = rischio?.summary ?? {}
  const fasce: Array<[string, string]> = [
    ['critico', 'var(--error)'],
    ['alto', 'var(--warn)'],
    ['medio', 'var(--info)'],
    ['basso', 'var(--text-muted)'],
    ['nessun rilievo', 'var(--ok)'],
  ]
  const totale = Object.values(sintesi).reduce((a, b) => a + b, 0)

  const aperti = reperti.filter((r) => !r.accepted)
  const sfruttate = aperti.filter((r) => r.evidence?.sfruttata === true).length
  const gravi = aperti.filter(
    (r) => r.severity === 'critica' || r.severity === 'alta',
  ).length
  const ultima = rischio?.assets?.reduce<string | null>(
    (max, a) => (a.assessed_at && (!max || a.assessed_at > max) ? a.assessed_at : max),
    null,
  )

  if (totale === 0 && aperti.length === 0) return null

  return (
    <div className="row" style={{ alignItems: 'stretch' }}>
      <Card title={t('Rischio del parco')}>
        <Donut
          centreLabel={t('Asset')}
          slices={fasce.map(([banda, colore]) => ({
            key: banda,
            label: maiuscola(t(banda)),
            value: sintesi[banda] ?? 0,
            color: colore,
          }))}
        />
      </Card>
      <div className="stats" style={{ flex: 1, marginBottom: 20 }}>
        <StatCard
          label={t('Sfruttate attivamente (CISA KEV)')}
          value={sfruttate}
          tone={sfruttate > 0 ? 'error' : 'ok'}
          hint={t('Vulnerabilità che qualcuno sta già usando: prima di tutto il resto')}
        />
        <StatCard
          label={t('Rischi critici e alti')}
          value={gravi}
          tone={gravi > 0 ? 'warn' : 'ok'}
          hint={t('Su {totale} rischi aperti', { totale: aperti.length })}
        />
        <StatCard
          label={t('Ultima valutazione')}
          value={ultima ? formatDateTime(ultima) : '—'}
          hint={t('Il silenzio vale quanto è fresco')}
        />
      </div>
    </div>
  )
}

/**
 * I reperti: cosa, degli asset, non va.
 *
 * Ogni riga porta **la prova**: da quale fatto viene. Un reperto che non si può
 * verificare è un'opinione, e le opinioni di un programma non le legge nessuno
 * due volte.
 */
function Reperti({
  stato,
  conAccettati,
  onConAccettati,
}: {
  stato: { data: SecurityFinding[] | null; error: unknown; loading: boolean; reload: () => Promise<void> }
  conAccettati: boolean
  onConAccettati: (v: boolean) => void
}) {
  const reperti = stato
  const [aperto, setAperto] = useState<string | null>(null)

  // Il filtro per gravità e la ricerca: su qualche centinaio di righe «quali
  // sono le critiche di quel server» deve essere due clic, non una scorsa.
  const [gravita, setGravita] = useState<string | null>(null)

  const tutte = reperti.data ?? []
  const righe = tutte.filter((r) => !gravita || r.severity === gravita)

  const colonne: Column<SecurityFinding>[] = [
    {
      key: 'severity',
      header: t('Gravità'),
      value: (r) => maiuscola(t(r.severity)),
      render: (r) => <GravitaBadge gravita={r.severity} />,
    },
    {
      key: 'title',
      header: t('Cosa'),
      value: (r) => `${t(r.title, r.evidence)}${r.accepted ? ` ${t('rischio accettato')}` : ''}`,
      render: (r) => (
        <>
          <TestoConKev testo={t(r.title, r.evidence)} cve={cveDaProva(r.evidence)} />
          {r.accepted && (
            <>
              {' '}
              <span className="badge badge--muted">{t('rischio accettato')}</span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'ci_name',
      header: t('Asset'),
      value: (r) => r.ci_name ?? r.ci_id.slice(0, 8),
      render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.ci_name ?? r.ci_id.slice(0, 8)}</Link>,
    },
    {
      key: 'first_seen',
      header: t('Aperto dal'),
      type: 'date',
      render: (r) => formatDateTime(r.first_seen),
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      filter: false,
      searchable: false,
      align: 'right',
      render: (r) => (
        <button className="link" onClick={() => setAperto(aperto === r.id ? null : r.id)}>
          {aperto === r.id ? t('Nascondi') : t('Dettagli')}
        </button>
      ),
    },
  ]

  return (
    <Card
      title={
        tutte.length > 0
          ? t('Dettaglio rischio ({n})', { n: tutte.length })
          : t('Dettaglio rischio')
      }
      actions={
        <label className="field__inline">
          <input
            type="checkbox"
            checked={conAccettati}
            onChange={(e) => onConAccettati(e.target.checked)}
          />{' '}
          {t('Mostra anche i rischi accettati')}
        </label>
      }
    >
      <ErrorBanner error={reperti.error} />
      {reperti.loading && !reperti.data ? (
        <Spinner />
      ) : tutte.length === 0 ? (
        <EmptyState
          title={t('Nessun rischio')}
          // «Niente da segnalare» e «non ho ancora guardato» si somigliano
          // fino a essere indistinguibili, e portano a due decisioni opposte.
          hint={t(
            'Nessuna raccolta ha ancora prodotto rischi. Il rapporto di ogni job dice se la valutazione è stata eseguita e con quale conoscenza: un elenco vuoto vale quanto vale quel rapporto.',
          )}
        />
      ) : (
        <div>
          {/* Il riepilogo per gravità in cima: su qualche centinaio di righe la
              prima domanda è «quante sono gravi», non «quali sono». Senza, si
              scorre tutto per scoprire che erano tutte medie. */}
          {/* I conteggi per gravità sono anche il filtro: gli stessi numeri
              che prima si leggevano soltanto, ora si cliccano. Un filtro che
              non mostra i conteggi fa scegliere alla cieca; dei conteggi che
              non filtrano fanno cercare a mano ciò che era già contato. */}
          <p className="filtro-gravita">
            {(['critica', 'alta', 'media', 'bassa'] as const).map((g) => {
              const quante = tutte.filter((r) => r.severity === g).length
              if (quante === 0) return null
              return (
                <button
                  type="button"
                  key={g}
                  className={`chip${gravita === g ? ' chip--attivo' : ''}`}
                  style={{ cursor: 'pointer' }}
                  aria-pressed={gravita === g}
                  onClick={() => setGravita(gravita === g ? null : g)}
                >
                  <span className={classeGravita(g)}>
                    {quante} {t(g)}
                  </span>
                </button>
              )
            })}
            <span className="muted">
              {t('su {asset} asset', { asset: new Set(tutte.map((r) => r.ci_id)).size })}
            </span>
          </p>

          <DataTable
            rows={righe}
            columns={colonne}
            rowKey={(r) => r.id}
            conteggio={{ nome: t('rischi') }}
            emptyText={t('Nessun rischio corrisponde al filtro.')}
            expand={(r) => <Prova reperto={r} onCambiato={() => reperti.reload()} />}
            expanded={(r) => aperto === r.id}
            // The coloured stripe on the left of the row, by severity.
            rowClassName={(r) => `riga-reperto riga-reperto--${r.severity}`}
          />
        </div>
      )}
    </Card>
  )
}

/** Il dettaglio: perché, da cosa, e cosa fare. */
function Prova({
  reperto,
  onCambiato,
}: {
  reperto: SecurityFinding
  onCambiato: () => void
}) {
  const [motivo, setMotivo] = useState('')
  const [giorni, setGiorni] = useState(90)
  const [errore, setErrore] = useState<unknown>(null)

  return (
    <div className="panel">
      {/* Titolo e motivo sono modelli con segnaposto, e i valori stanno
          nella prova: è lo stesso meccanismo dei messaggi dei rapporti. */}
      {reperto.why && <p style={{ marginTop: 0 }}>{t(reperto.why, reperto.evidence)}</p>}

      <dl className="kv">
        <dt>{t('Da quale fatto')}</dt>
        <dd>
          {/* La prova alla lettera, non riscritta: è ciò che si va a
              controllare quando il reperto sembra sbagliato, e riformularla
              vorrebbe dire togliere proprio la parte verificabile. */}
          <code className="mono">{JSON.stringify(reperto.evidence)}</code>
        </dd>
        <dt>{t('Regola')}</dt>
        <dd>
          <code className="mono">{reperto.rule}</code>
        </dd>
        {reperto.remedy && (
          <>
            <dt>{t('Rimedio')}</dt>
            <dd>{t(reperto.remedy)}</dd>
          </>
        )}
      </dl>

      <ErrorBanner error={errore} />

      {reperto.accepted ? (
        <p className="muted">
          {t('Rischio accettato: {motivo}', { motivo: reperto.accepted_reason ?? '' })}
          {reperto.accepted_until && (
            <>
              {' — '}
              {t('torna visibile il {quando}', {
                quando: formatDateTime(reperto.accepted_until),
              })}
            </>
          )}{' '}
          <button
            className="link"
            onClick={async () => {
              try {
                await api.revokeFindingAcceptance(reperto.id)
                onCambiato()
              } catch (e) {
                setErrore(e)
              }
            }}
          >
            {t('Revoca')}
          </button>
        </p>
      ) : (
        <div className="form-grid">
          <label htmlFor={`motivo-${reperto.id}`}>{t('Accetta il rischio, perché')}</label>
          <input
            id={`motivo-${reperto.id}`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={t('es. apparato del fornitore, sostituzione già pianificata')}
          />
          <label htmlFor={`giorni-${reperto.id}`}>{t('Per quanti giorni')}</label>
          <input
            id={`giorni-${reperto.id}`}
            type="number"
            min={1}
            max={365}
            value={giorni}
            onChange={(e) => setGiorni(Number(e.target.value))}
          />
          <div />
          <div>
            {/* La scadenza è obbligatoria e il campo non si può svuotare:
                un'accettazione senza scadenza è un modo di nascondere le cose
                per sempre, che è la stessa cosa di non vederle. */}
            <button
              disabled={motivo.trim().length < 3}
              onClick={async () => {
                try {
                  await api.acceptFinding(reperto.id, motivo.trim(), giorni)
                  onCambiato()
                } catch (e) {
                  setErrore(e)
                }
              }}
            >
              {t('Accetta')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Il rischio del parco: quanti asset in ciascuna fascia, e i peggiori per nome.
 *
 * ## Perché sta in cima
 *
 * Perché è la domanda con cui si apre questa pagina — «quanto sto messo male, e
 * da dove comincio» — e le due sotto (le azioni, i reperti) rispondono a «cosa
 * faccio» e «cosa c'è». In quest'ordine: chi apre la pagina non sa ancora se
 * deve preoccuparsi.
 *
 * ## Perché fasce e non un punteggio
 *
 * Un numero da zero a cento sembra una misura e non lo è: si cita nelle
 * riunioni e si mette nei contratti, e non regge il peso di come è stato
 * calcolato. Le fasce si difendono una per una, e ognuna arriva con il suo
 * motivo — «critico» da solo si può solo credere, «critico: CVE-2021-44228 è
 * sfruttata attivamente (CISA KEV)» si controlla.
 */
function Rischio({
  dati,
  caricamento,
}: {
  dati: SecurityRisk | undefined | null
  caricamento: boolean
}) {
  if (caricamento && !dati) {
    return (
      <Card title={t('Da dove cominciare')}>
        <Spinner />
      </Card>
    )
  }
  if (!dati || dati.assets.length === 0) return null

  // Assets come ordered by risk: the first page answers «where do I start».
  const colonneRischio: Column<SecurityRisk['assets'][number]>[] = [
    {
      key: 'band',
      header: t('Fascia'),
      value: (a) => maiuscola(t(a.band)),
      render: (a) => <span className={classeFascia(a.band)}>{maiuscola(t(a.band))}</span>,
    },
    {
      key: 'ci_name',
      header: t('Asset'),
      value: (a) => a.ci_name ?? a.ci_id.slice(0, 8),
      render: (a) => <Link to={`/cis/${a.ci_id}`}>{a.ci_name ?? a.ci_id.slice(0, 8)}</Link>,
    },
    {
      key: 'service',
      header: t('Servizio'),
      value: (a) =>
        a.service_criticality
          ? `${CRITICALITY_LABEL[a.service_criticality] ?? a.service_criticality}${a.services?.length ? ` ${a.services.join(', ')}` : ''}`
          : '',
      // Inherited from business services: same band, the components of the
      // most critical services come first.
      render: (a) =>
        a.service_criticality ? (
          <>
            {CRITICALITY_LABEL[a.service_criticality] ?? a.service_criticality}
            {a.services?.length ? <><br /><small className="text-muted">{a.services.join(', ')}</small></> : null}
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'reasons',
      header: t('Perché'),
      value: (a) => a.reasons.map((m) => motivoRischio(m, a)).join(', '),
      render: (a) => a.reasons.map((m) => <div key={m}>{motivoRischio(m, a)}</div>),
    },
    {
      key: 'assessed_at',
      header: t('Valutato'),
      type: 'date',
      // The date travels with the band: «no findings» three months ago is not
      // «no findings».
      render: (a) => (a.assessed_at ? formatDateTime(a.assessed_at) : '—'),
    },
  ]

  return (
    <Card title={t('Da dove cominciare')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Asset ordinati per livello di rischio, con priorità rivolta ai Business Service più critici.')}
      </p>

      <DataTable
        rows={dati.assets}
        columns={colonneRischio}
        rowKey={(a) => a.ci_id}
        conteggio={{ nome: t('asset') }}
      />
    </Card>
  )
}

/** One reason of a risk band, with its counts filled in. */
function motivoRischio(m: string, a: SecurityRisk['assets'][number]): string {
  return t(m, {
    n: a.counts.exploited,
    c: a.counts.critical,
    a: a.counts.high,
    x: a.counts.other,
    p: a.counts.other,
  })
}

/** Il colore di una fascia di rischio. */
function classeFascia(fascia: string): string {
  switch (fascia) {
    case 'critico':
      return 'badge badge--error'
    case 'alto':
      return 'badge badge--warn'
    case 'medio':
      return 'badge badge--info'
    default:
      return 'badge badge--muted'
  }
}

/**
 * Il software che non riceve più correzioni.
 *
 * ## Perché una sezione sua e non righe fra le altre
 *
 * Perché il rimedio è di un'altra natura. Una vulnerabilità si chiude con un
 * aggiornamento e si mette in coda alla manutenzione; un prodotto fuori
 * supporto **non ha un aggiornamento da aspettare** — va sostituito, e questo è
 * un progetto, non un comando.
 *
 * Messi insieme, i secondi sparirebbero fra i primi: sono pochi e non urgenti,
 * e l'urgenza decide l'ordine dell'elenco accanto.
 *
 * ## Perché per prodotto e non per asset
 *
 * Perché la domanda è «quanti Tomcat 8.5 ho in giro»: il rimedio è uno solo e
 * vale per tutti. Un elenco per asset obbligherebbe a ricostruirlo a mente.
 */
function FineSupporto() {
  const eos = useAsync(() => api.endOfSupport(), [])
  const dati = eos.data

  if (eos.loading && !dati) {
    return (
      <Card title={t('Fuori supporto')}>
        <Spinner />
      </Card>
    )
  }
  if (!dati || dati.products.length === 0) return null

  const colonneFineSupporto: Column<EndOfSupport['products'][number]>[] = [
    {
      key: 'product',
      header: t('Prodotto'),
      value: (p) => `${p.product}${p.is_operating_system ? ` ${t('sistema')}` : ''}`,
      render: (p) => (
        <>
          {p.product}
          {p.is_operating_system && (
            <>
              {' '}
              <span className="badge badge--muted">{t('sistema')}</span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'cycle',
      header: t('Ciclo'),
      // The last version of the cycle says **how far one could go**: without
      // it, «out of support» does not tell who stopped at the first version
      // from who followed up to the last.
      render: (p) => (
        <>
          {p.cycle || '—'}
          {p.latest_in_cycle && (
            <span className="muted"> {t('(ultima: {versione})', { versione: p.latest_in_cycle })}</span>
          )}
        </>
      ),
    },
    { key: 'end_of_support', header: t('Fuori supporto dal'), type: 'date' },
    {
      key: 'assets',
      header: t('Su quanti asset'),
      value: (p) => p.assets.map((a) => a.ci_name ?? a.ci_id.slice(0, 8)).join(', '),
      render: (p) => (
        <>
          {p.assets.slice(0, 3).map((a) => (
            <div key={a.ci_id}>
              <Link to={`/cis/${a.ci_id}`}>{a.ci_name ?? a.ci_id.slice(0, 8)}</Link>
              {a.version && <span className="muted"> {a.version}</span>}
            </div>
          ))}
          {p.assets.length > 3 && (
            <span className="muted">{t('e altri {altri}', { altri: p.assets.length - 3 })}</span>
          )}
        </>
      ),
    },
  ]

  return (
    <Card title={t('Fuori supporto ({n})', { n: dati.products.length })}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Prodotti senza più disponibilità di aggiornamenti di sicurezza, da sostituire.')}
      </p>
      <DataTable
        rows={dati.products}
        columns={colonneFineSupporto}
        rowKey={(p) => p.product + p.cycle}
        conteggio={{ nome: t('prodotti') }}
      />
    </Card>
  )
}

/**
 * Cosa fare, prima di cosa c'è.
 *
 * Ottanta righe di reperti sono un elenco di fatti: chi le legge deve ancora
 * capire da solo cosa fargli. Su un host di distribuzione la risposta è quasi
 * sempre la stessa per tutte e ottanta — aggiornare i pacchetti — e una riga
 * alla volta non si vede.
 */
function CosaFare({ righe }: { righe: SecurityFinding[] }) {
  // **Un blocco per asset**, e non è una scelta di impaginazione.
  //
  // Le azioni si compongono mettendo insieme i pacchetti di più reperti: fatto
  // su tutto il parco produrrebbe un comando solo con dentro i pacchetti di
  // macchine diverse — e magari di distribuzioni diverse, quindi con il
  // gestore sbagliato. Chi lo incolla su un host aggiorna cose che quell'host
  // non ha, e non aggiorna quelle che ha.
  //
  // È il difetto peggiore possibile per una riga che invita a essere eseguita.
  const perAsset = new Map<string, { nome: string; righe: SecurityFinding[] }>()
  for (const r of righe) {
    const voce = perAsset.get(r.ci_id) ?? {
      nome: r.ci_name ?? r.ci_id.slice(0, 8),
      righe: [],
    }
    voce.righe.push(r)
    perAsset.set(r.ci_id, voce)
  }

  const blocchi = [...perAsset.entries()]
    .map(([id, voce]) => ({ id, nome: voce.nome, azioni: azioniDa(voce.righe) }))
    .filter((b) => b.azioni.length > 0)
    // Prima l'asset che ha la cosa più grave: si comincia da lì.
    .sort((a, b) => PESO_GRAVITA(a.azioni[0].gravita) - PESO_GRAVITA(b.azioni[0].gravita))

  if (blocchi.length === 0) return null

  return (
    <Card title={t('Cosa fare')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Azioni correttive, da applicare per risolvere i rischi rilevati per singolo asset, ordinate per priorità.')}
      </p>
      {blocchi.map((b) => (
        <div key={b.id} className="azioni__asset">
          <p className="azioni__titolo">
            <Link to={`/cis/${b.id}`}>{b.nome}</Link>
          </p>
          <ElencoAzioni azioni={b.azioni} />
        </div>
      ))}
    </Card>
  )
}

/** L'ordine delle gravità, per ordinare gli asset. */
function PESO_GRAVITA(g: string): number {
  return { critica: 0, alta: 1, media: 2, bassa: 3 }[g] ?? 9
}

/** Le azioni di un asset, dalla più urgente. */
function ElencoAzioni({ azioni }: { azioni: Azione[] }) {
  return (
    <ol className="azioni">
      {azioni.map((a) => (
        <li key={a.cosa + (a.comando ?? '')}>
          <span className={classeGravita(a.gravita)}>{t(a.gravita)}</span>{' '}
          {t(a.cosa, a.valori)}{' '}
          <span className="muted">
            {t('— chiude {chiude} rischi', { chiude: a.chiude })}
          </span>
          {a.comando && (
            // Il comando per intero e selezionabile: si incolla. Troncarlo per
            // estetica aggiornerebbe meno di quanto la riga sopra promette, e
            // chi lo esegue crederebbe di aver finito.
            <pre className="comando">{a.comando}</pre>
          )}
        </li>
      ))}
    </ol>
  )
}

function GravitaBadge({ gravita }: { gravita: string }) {
  return <span className={classeGravita(gravita)}>{maiuscola(t(gravita))}</span>
}

/** Il colore della gravità. Le fasce sono quelle di CVSS, che le dichiara. */
function classeGravita(gravita: string): string {
  switch (gravita) {
    case 'critica':
      return 'badge badge--error'
    case 'alta':
      return 'badge badge--warn'
    case 'media':
      return 'badge badge--info'
    default:
      return 'badge badge--muted'
  }
}

function Segnalati({
  righe,
  caricamento,
}: {
  righe: ConfigurationItem[] | null
  caricamento: boolean
}) {
  if (caricamento && !righe) {
    return (
      <Card title={t('Asset segnalati')}>
        <Spinner />
      </Card>
    )
  }

  const elenco = righe ?? []
  if (elenco.length === 0) {
    return (
      <Card title={t('Asset segnalati')}>
        <EmptyState
          title={t('Nessun asset segnalato')}
          // «Nessuno» e «non ho ancora guardato» sono due cose diverse con due
          // rimedi diversi, e l'elenco vuoto da solo si legge come la prima.
          hint={t(
            'Nessuna evidenza rilevata dalle sorgenti di sicurezza, l’assenza di segnalazioni non indica assenza di rischi.',
          )}
        />
      </Card>
    )
  }

  const motivi = (ci: ConfigurationItem): unknown =>
    ci.attributes[ATTRIBUTO_SEGNALATO.replace('flagged', 'flag_reasons')]
  const colonneSegnalati: Column<ConfigurationItem>[] = [
    {
      key: 'name',
      header: t('Asset'),
      value: (ci) => ci.name ?? ci.primary_key,
      render: (ci) => <Link to={`/cis/${ci.id}`}>{ci.name ?? ci.primary_key}</Link>,
    },
    {
      key: 'reasons',
      header: t('Perché è segnalato'),
      value: (ci) => {
        const v = motivi(ci)
        return Array.isArray(v) ? v.map((m) => t(String(m))).join(', ') : t('motivo non riportato')
      },
      render: (ci) => <Motivi valore={motivi(ci)} />,
    },
    // The score is a number and stays a number: ntopng declares no scale nor
    // maximum, and colouring it here would give it an authority nobody has.
    {
      key: 'ntopng_score',
      header: t('Punteggio ntopng'),
      type: 'number',
      value: (ci) => (typeof ci.attributes.ntopng_score === 'number' ? ci.attributes.ntopng_score : null),
      render: (ci) => numero(ci.attributes.ntopng_score),
    },
    {
      key: 'ntopng_alerts',
      header: t('Allarmi su ntopng'),
      type: 'number',
      value: (ci) => (typeof ci.attributes.ntopng_alerts === 'number' ? ci.attributes.ntopng_alerts : null),
      render: (ci) => numero(ci.attributes.ntopng_alerts),
    },
    {
      key: 'last_seen',
      header: t('Ultimo rilevamento'),
      type: 'date',
      render: (ci) => (ci.last_seen ? formatDateTime(ci.last_seen) : '—'),
    },
  ]

  return (
    <Card title={t('Asset segnalati ({n})', { n: elenco.length })}>
      <DataTable
        rows={elenco}
        columns={colonneSegnalati}
        rowKey={(ci) => ci.id}
        conteggio={{ nome: t('asset') }}
      />
    </Card>
  )
}

/** I motivi in chiaro, uno per riga. */
function Motivi({ valore }: { valore: unknown }) {
  if (!Array.isArray(valore) || valore.length === 0) {
    // Segnalato senza motivo non dovrebbe succedere: la sonda scrive i due
    // campi insieme. Dirlo invece di lasciare la cella vuota, perché una cella
    // vuota si legge come «nessun motivo» e non come «motivo perduto».
    return <span className="muted">{t('motivo non riportato')}</span>
  }
  return (
    <>
      {valore.map((m) => (
        <span className="badge badge--warn" key={String(m)}>
          {t(String(m))}
        </span>
      ))}
    </>
  )
}

function numero(valore: unknown): string {
  return typeof valore === 'number' ? String(valore) : '—'
}
